// SPDX-License-Identifier: MIT
// Execution Status Component - Loading, error, and empty states for query results

import React from 'react';
import styled, { keyframes } from 'styled-components';
import type { DefaultTheme } from 'styled-components';

// Animations
const dotFlashing = keyframes`
  0% { opacity: 0.2; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
  100% { opacity: 0.2; transform: scale(0.8); }
`;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
  text-align: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const LoadingText = styled.div`
  color: ${props => props.theme.primaryColor};
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const LoadingDots = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.div<{ $delay: number }>`
  width: 10px;
  height: 10px;
  background: ${props => props.theme.primaryColor};
  border-radius: 50%;
  animation: ${dotFlashing} 1s infinite;
  animation-delay: ${props => props.$delay}s;
  animation-delay: ${props => props.$delay}s;
`;

const ErrorContainer = styled.div`
  max-width: 500px;
  padding: 16px;
  background: ${props => props.theme.errorColor || '#d73527'}10;
  border: 2px solid ${props => props.theme.errorColor || '#d73527'};
  border-radius: 8px;
`;

const ErrorIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  color: ${props => props.theme.errorColor || '#d73527'};
`;

const ErrorTitle = styled.div`
  color: ${props => props.theme.errorColor || '#d73527'};
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
  letter-spacing: -0.01em;
`;

const ErrorMessage = styled.pre`
  color: ${props => props.theme.textColor};
  font-size: 12px;
  font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
  background: ${props => props.theme.panelBackground};
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  text-align: left;
  max-height: 200px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.borderColor}60;
    border-radius: 3px;
  }
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
  color: ${props => props.theme.primaryColor};
`;

const EmptyText = styled.div`
  color: ${props => props.theme.textColor};
  font-size: 15px;
  font-weight: 500;
  line-height: 1.6;
`;

const HintText = styled.div`
  color: ${props => props.theme.subtextColor};
  font-size: 12px;
  margin-top: 12px;
  opacity: 0.7;
`;

// Props interface
interface ExecutionStatusProps {
  status: 'loading' | 'error' | 'empty';
  message?: string;
  theme: DefaultTheme;
}

export const ExecutionStatus: React.FC<ExecutionStatusProps> = ({ status, message, theme }) => {
  if (status === 'loading') {
    return (
      <Container data-testid='execution-loading'>
        <LoadingContainer>
          <LoadingText theme={theme}>Executing query</LoadingText>
          <LoadingDots>
            <Dot $delay={0} />
            <Dot $delay={0.15} />
            <Dot $delay={0.3} />
          </LoadingDots>
        </LoadingContainer>
      </Container>
    );
  }

  if (status === 'error') {
    return (
      <Container data-testid='execution-error'>
        <ErrorContainer theme={theme}>
          <ErrorIcon>
            <svg viewBox='0 0 24 24' fill='currentColor' width='32' height='32'>
              <path d='M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z' />
            </svg>
          </ErrorIcon>
          <ErrorTitle theme={theme}>Query Error</ErrorTitle>
          <ErrorMessage theme={theme}>{message || 'An unknown error occurred'}</ErrorMessage>
        </ErrorContainer>
      </Container>
    );
  }

  // Empty state
  return (
    <Container data-testid='execution-empty'>
      <EmptyIcon>
        <svg viewBox='0 0 24 24' fill='currentColor'>
          <path d='M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z' />
        </svg>
      </EmptyIcon>
      <EmptyText theme={theme}>
        {message || 'Write a SQL query and press Run to see results'}
      </EmptyText>
      <HintText theme={theme}>
        Use the <strong>Run</strong> button above the editor to execute your query.
      </HintText>
    </Container>
  );
};

export default ExecutionStatus;
